import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import { CSVLink } from 'react-csv';
import { jwtDecode } from "jwt-decode";
import { useTranslation } from 'react-i18next';
import {
  Table,
  Card,
  Select,
  Button,
  Space,
  Typography,
  Tag,
  message,
  Tooltip,
  Popconfirm,
  Row,
  Col,
  Statistic
} from 'antd';
import {
  FileExcelOutlined,
  CalendarOutlined,
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  BookOutlined,
  CheckOutlined,
  CloseOutlined
} from '@ant-design/icons';
import moment from 'moment';

const { Text } = Typography;
const { Option } = Select;



const calculateEndTime = (startTime, duration) => {
  if (!startTime || !duration) return '';

  const [startHour, startMinute] = startTime.split(':').map(Number);

  // Support formats like "2h 30m" or "1.5"
  const match = duration.match(/(\d+)h\s*(\d+)?m?/);
  let hours = 0, minutes = 0;

  if (match) {
    hours = parseInt(match[1], 10);
    minutes = parseInt(match[2] || '0', 10);
  }

  let endHour = startHour + hours;
  let endMinute = startMinute + minutes;

  if (endMinute >= 60) {
    endHour += Math.floor(endMinute / 60);
    endMinute %= 60;
  }

  return `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`;
};

const parseScheduleHour = (scheduleHour) => {
  if (!scheduleHour) return 0;
  
  // Handle formats like "2h 30m", "1h", "1.5", "90m", "1:30"
  const hourMinuteMatch = scheduleHour.match(/(\d+)h\s*(\d+)?m?/);
  const decimalMatch = scheduleHour.match(/^(\d+\.?\d*)$/);
  const minuteMatch = scheduleHour.match(/^(\d+)m$/);
  const timeMatch = scheduleHour.match(/^(\d+):(\d+)$/);
  
  if (hourMinuteMatch) {
    const hours = parseInt(hourMinuteMatch[1], 10);
    const minutes = parseInt(hourMinuteMatch[2] || '0', 10);
    return hours * 60 + minutes;
  } else if (decimalMatch) {
    return Math.round(parseFloat(decimalMatch[1]) * 60);
  } else if (minuteMatch) {
    return parseInt(minuteMatch[1], 10);
  } else if (timeMatch) {
    const hours = parseInt(timeMatch[1], 10);
    const minutes = parseInt(timeMatch[2], 10);
    return hours * 60 + minutes;
  }
  
  return 0;
};

// 2. Add this function to format minutes back to readable format
const formatMinutesToHours = (totalMinutes) => {
  if (totalMinutes === 0) return '0h';
  
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  
  if (minutes === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${minutes}m`;
  }
};

// NEW: Convert minutes to decimal hours for CSV export
const formatMinutesToDecimal = (totalMinutes) => {
  if (totalMinutes === 0) return '0';
  const decimalHours = totalMinutes / 60;
  return decimalHours.toFixed(2);
};

// 3. Calculate total hours from filtered data (add this where you calculate summaryStats)
const calculateTotalHours = (data) => {
  const totalMinutes = data.reduce((sum, cls) => {
    return sum + parseScheduleHour(cls.schedule_hour);
  }, 0);
  return totalMinutes;
};


const TutorClassLog = () => {
  const { t } = useTranslation();
  const [classes, setClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branchFilter, setBranchFilter] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branches, setBranches] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 100,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ['50', '100', '150'],
    showTotal: (total) => `${t('Total')}: ${total} ${t('classes')}`,
  });
  const today = moment().format('YYYY-MM-DD');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);

  // Get the current month and year in 'YYYY-M' format
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // Months are 0-indexed, so add 1
  const defaultMonthYear = `${currentYear}-${currentMonth}`;

  const [selectedMonthYear, setSelectedMonthYear] = useState(defaultMonthYear);
  const [statusFilter, setStatusFilter] = useState(null);
  const [adminapproveFilter, setAdminapproveFilter] = useState(null);
  const [tutorFilter, setTutorFilter] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL;

  const [summaryStats, setSummaryStats] = useState({
    totalClasses: 0,
    confirmedClasses: 0,
    pendingClasses: 0,
    adminConfirmed: 0
  });
const token = localStorage.getItem("token");
let userRole = null;
let userBranchId = null;

if (token) {
  try {
    const decoded = jwtDecode(token);
    userRole = decoded.role;
    userBranchId = decoded.branchId || decoded.branch_id || null;
  } catch (err) {
    console.error("Invalid token", err);
  }
}


useEffect(() => {
  const fetchBranches = async () => {
    try {
      if (userRole !== "superadmin") return; // ✅ Only superadmin fetches all branches
      const res = await axios.get(`${API_URL}/branch`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranches(res.data);
    } catch (err) {
      console.error("Failed to fetch branches", err);
    }
  };
  fetchBranches();
}, [API_URL, token, userRole]);



const fetchClasses = useCallback(async () => {
  setLoading(true);
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      console.error("❌ No token found");
      setLoading(false);
      return;
    }

    const decoded = jwtDecode(token);
    const role = decoded.role;
    const branchId = decoded.branchId || decoded.branch_id || null;

    // ✅ Always include date range
    const params = {
      startDate,
      endDate,
    };

    // ✅ Restrict admin to their branch
    if (role === "admin" && branchId) {
      params.branchId = branchId;
    }

    // ✅ Allow superadmin to choose branch
    if (role === "superadmin" && selectedBranch) {
      params.branchFilter = selectedBranch;
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/classes/class-by-day`,
      {
        headers: { Authorization: `Bearer ${token}` },
        params,
      }
    );

    setClasses(response.data);
    setFilteredClasses(response.data);
    calculateSummaryStats(response.data);
  } catch (error) {
    console.error("Error fetching classes:", error);
    message.error(t("Failed to fetch classes"));
  } finally {
    setLoading(false);
  }
}, [t, startDate, endDate, selectedBranch]);


  
  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);
  

 const calculateSummaryStats = (data) => {
  const stats = {
    totalClasses: data.length,
    confirmedClasses: data.filter(cls => cls.status === 'approved').length,
    pendingClasses: data.filter(cls => cls.status !== 'approved').length,
    adminConfirmed: data.filter(cls => cls.adminapprove === 'adminconfirm').length,
    totalHours: calculateTotalHours(data) // Add this line
  };
  setSummaryStats(stats);
};

useEffect(() => {
  const applyFilters = () => {
    const filtered = classes
      .filter((cls) => {
        // Use only the date part to avoid timezone shift issues
        const classDateStr = moment(cls.date).format('YYYY-MM-DD');
        const classDate = moment(classDateStr, 'YYYY-MM-DD');

        const startStr = startDate ? moment(startDate).format('YYYY-MM-DD') : null;
        const endStr = endDate ? moment(endDate).format('YYYY-MM-DD') : null;

        const isInRange =
          (!startStr || classDate.isSameOrAfter(startStr, 'day')) &&
          (!endStr || classDate.isSameOrBefore(endStr, 'day'));

        const isStatusMatch = statusFilter ? cls.status === statusFilter : true;
        const isAdminapproveMatch = adminapproveFilter ? cls.adminapprove === adminapproveFilter : true;
        
        // FIX #1: Changed from cls.nickname to cls.teacher_nickname
        const isTutorMatch = tutorFilter ? cls.teacher_nickname === tutorFilter : true;

        // ✅ Branch filter (match branch_id if branchFilter is selected)
        const isBranchMatch = branchFilter ? String(cls.branch_id) === String(branchFilter) : true;

        return (
          isInRange &&
          isStatusMatch &&
          isAdminapproveMatch &&
          isTutorMatch &&
          isBranchMatch
        );
      })
      .sort((a, b) => {
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        if (dateA !== dateB) return dateA - dateB;

        const timeA = new Date(`1970-01-01T${a.schedule_time}`).getTime();
        const timeB = new Date(`1970-01-01T${b.schedule_time}`).getTime();
        return timeA - timeB;
      });

    setFilteredClasses(filtered);
    setPagination((prev) => ({
      ...prev,
      current: 1,
      total: filtered.length,
    }));

    calculateSummaryStats(filtered);
  };

  applyFilters();
}, [startDate, endDate, statusFilter, adminapproveFilter, tutorFilter, branchFilter, classes]);



  const getUniqueTutors = () => {
    // FIX #1: Changed from cls.nickname to cls.teacher_nickname
    const tutors = classes.map(cls => cls.teacher_nickname).filter(Boolean);
    return [...new Set(tutors)].sort();
  };

const handleAdminApprove = async (classId, status) => {
  try {
    const token = localStorage.getItem('token');
    await axios.put(
      `${process.env.REACT_APP_API_URL}/classes/${classId}/adminapprove`,
      { adminapprove: status },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    message.success(t('Class approved successfully'));

    // ✅ Update BOTH classes and filteredClasses
    setClasses(prevClasses => {
      const updated = prevClasses.map(cls =>
        cls.classid === classId ? { ...cls, adminapprove: status } : cls
      );
      
      // ✅ Also update filteredClasses based on the updated classes
      const filtered = updated.filter((cls) => {
        const classDateStr = moment(cls.date).format('YYYY-MM-DD');
        const classDate = moment(classDateStr, 'YYYY-MM-DD');

        const startStr = startDate ? moment(startDate).format('YYYY-MM-DD') : null;
        const endStr = endDate ? moment(endDate).format('YYYY-MM-DD') : null;

        const isInRange =
          (!startStr || classDate.isSameOrAfter(startStr, 'day')) &&
          (!endStr || classDate.isSameOrBefore(endStr, 'day'));

        const isStatusMatch = statusFilter ? cls.status === statusFilter : true;
        const isAdminapproveMatch = adminapproveFilter ? cls.adminapprove === adminapproveFilter : true;
        const isTutorMatch = tutorFilter ? cls.teacher_nickname === tutorFilter : true;
        const isBranchMatch = branchFilter ? String(cls.branch_id) === String(branchFilter) : true;

        return isInRange && isStatusMatch && isAdminapproveMatch && isTutorMatch && isBranchMatch;
      });
      
      setFilteredClasses(filtered);
      calculateSummaryStats(filtered);
      
      return updated;
    });

  } catch (error) {
    console.error('Error confirming hours:', error);
    message.error(t('Failed to approve class'));
    fetchClasses(); // Refetch on error
  }
};

const handleAdminUnapprove = async (classId) => {
  try {
    const token = localStorage.getItem('token');
    await axios.put(
      `${process.env.REACT_APP_API_URL}/classes/${classId}/admin-unapprove`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );

    message.success(t('Admin approval revoked successfully'));

    // ✅ Update BOTH classes and filteredClasses
    setClasses(prevClasses => {
      const updated = prevClasses.map(cls =>
        cls.classid === classId
          ? { ...cls, adminapprove: 'pending', admin_nickname: null }
          : cls
      );
      
      // ✅ Apply filters to updated data
      const filtered = updated.filter((cls) => {
        const classDateStr = moment(cls.date).format('YYYY-MM-DD');
        const classDate = moment(classDateStr, 'YYYY-MM-DD');

        const startStr = startDate ? moment(startDate).format('YYYY-MM-DD') : null;
        const endStr = endDate ? moment(endDate).format('YYYY-MM-DD') : null;

        const isInRange =
          (!startStr || classDate.isSameOrAfter(startStr, 'day')) &&
          (!endStr || classDate.isSameOrBefore(endStr, 'day'));

        const isStatusMatch = statusFilter ? cls.status === statusFilter : true;
        const isAdminapproveMatch = adminapproveFilter ? cls.adminapprove === adminapproveFilter : true;
        const isTutorMatch = tutorFilter ? cls.teacher_nickname === tutorFilter : true;
        const isBranchMatch = branchFilter ? String(cls.branch_id) === String(branchFilter) : true;

        return isInRange && isStatusMatch && isAdminapproveMatch && isTutorMatch && isBranchMatch;
      });
      
      setFilteredClasses(filtered);
      calculateSummaryStats(filtered);
      
      return updated;
    });

  } catch (error) {
    console.error('Error revoking admin approval:', error);
    message.error(t('Failed to revoke admin approval'));
    fetchClasses();
  }
};



  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const formatTime = (timeString) => {
    return moment(`1970-01-01T${timeString}`).format('HH:mm');
  };

// FIX #2 & #3: Updated CSV data with student names and decimal hours
const csvData = [
  // Individual class records
  ...filteredClasses.map(cls => ({
    BranchID: cls.branch_id || '',
    BranchName: cls.branch_name || '',
    No: cls.classid,
    Date: new Date(cls.date).toLocaleDateString(),
    ClassCode: cls.class_code,
    ClassID: cls.classid,
    Subject: cls.subject_name,
    Teacher: `${cls.teacher_nickname || cls.nickname} (${cls.teacher_name})`,
    Time: formatTime(cls.schedule_time),
    ScheduleHour: formatMinutesToDecimal(parseScheduleHour(cls.schedule_hour)), // FIX #3: Decimal format
    Students: cls.student_names || '', // FIX #2: Added student names
    Status: cls.status === 'approved' ? t('confirmed') : t('confirmpending'),
    AdminApprove: cls.adminapprove === 'adminconfirm' ? t('Admin Confirmed') : t('Pending')
  })),
  
  // Empty separator row
  {
    BranchID: '',
    BranchName: '',
    No: '',
    Date: '',
    ClassCode: '',
    ClassID: '',
    Subject: '',
    Teacher: '',
    Time: '',
    ScheduleHour: '',
    Students: '',
    Status: '',
    AdminApprove: ''
  },
  
  // Summary row with totals
  {
    BranchID: '',
    BranchName: '',
    No: '',
    Date: '',
    ClassCode: '',
    ClassID: '',
    Subject: t('TOTAL'),
    Teacher: `${filteredClasses.length} ${t('classes')}`,
    Time: '',
    ScheduleHour: formatMinutesToDecimal(summaryStats.totalHours), // FIX #3: Decimal format for total
    Students: '',
    Status: `${summaryStats.confirmedClasses} ${t('confirmed')}`,
    AdminApprove: `${summaryStats.adminConfirmed} ${t('Admin Confirmed')}`
  }
];


  const formatDate = (dateString) => {
    return moment(dateString).format('DD MMM YYYY');
  };
    const branchColors = {
  1: "blue",
  2: "green",
  3: "magenta", 
};

  const columns = [
       {
      title: t('branch'),
      dataIndex: 'branch_name',
      key: 'branch_name',
      width: 120,
      render: (text, record) => {
        const color = branchColors[record.branch_id] || "default";
        return <Tag color={color}>{text || t("N/A")}</Tag>;
      }
    },
    {
      title: t('date'),
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date) => (
        <Space>
          <CalendarOutlined />
          <Text>{formatDate(date)}</Text>
        </Space>
      ),
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
    },
    {
  title: t('classcode'),
  dataIndex: 'class_code',
  key: 'classcode',
  width: 120,
  render: (code) => (
    <Text
      style={{
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        lineHeight: 1.2,
      }}
    >
      {code || '—'}
    </Text>
  ),
},
    {
      title: t('classid'),
      dataIndex: 'classid',
      key: 'classid',
      width: 80,
    },
    {
      title: t('subject'),
      dataIndex: 'subject_name',
      key: 'subject',
      width: 150,
      render: (subject) => (
        <Tooltip title={subject}>
          <Space>
            <BookOutlined />
            <Text ellipsis style={{ maxWidth: 350 }}>{subject}</Text>
          </Space>
        </Tooltip>
      ),
    },
   {
  title: t('teacher'),
  dataIndex: 'teacher_nickname',
  key: 'teacher',
  width: 150,
  render: (nickname, record) => (
    <Space>
      <UserOutlined />
      <Text>{nickname || record.teacher_name || t('N/A')}</Text>
    </Space>
  ),
  // Table column filters are independent of the Select dropdown filter
  filters: getUniqueTutors().map(tutor => ({ text: tutor, value: tutor })),
  onFilter: (value, record) => record.teacher_nickname === value,
},

   {
        title: t('time'),
        dataIndex: 'schedule_time',
        key: 'time',
        width: 120,
        render: (time, record) => (
          <Space direction="vertical" size={0}>
            <Text>
              <ClockCircleOutlined /> {formatTime(time)} - {calculateEndTime(time, record.schedule_hour)}
            </Text>

          </Space>
        ),
      },

    {
      title: t('Hour'),
      dataIndex: 'schedule_hour',
      key: 'hour',
      width: 80,
      render: (hours) => <Tag color="purple">{hours}</Tag>,
    },
{
  title: t('students'),
  dataIndex: 'student_names',
  key: 'students',
  width: 220,
  render: (students) => {
    if (!students) {
      return (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t('No students')}
        </Text>
      );
    }

    // Split by comma and trim
    const studentList = students.split(',').map(s => s.trim()).filter(Boolean);

    return (
      <div
        style={{
          maxHeight: 100,
          overflowY: studentList.length > 4 ? 'auto' : 'visible',
          paddingRight: 4,
        }}
      >
        {studentList.map((name, idx) => (
          <div key={idx}>
            <Space size={4}>
              <UserOutlined style={{ color: '#1677ff' }} />
              <Text style={{ fontSize: 13 }}>{name}</Text>
            </Space>
          </div>
        ))}
        <div style={{ marginTop: 4 }}>
          <Tag color="geekblue">{studentList.length} {t('students')}</Tag>
        </div>
      </div>
    );
  },
},
{
  title: t('attendance'),
  dataIndex: 'attendance_list',
  key: 'attendance_list',
  width: 260,
  render: (attendanceList) => {
    if (!attendanceList || attendanceList.length === 0) {
      return (
        <Tag color="default" style={{ fontSize: 12 }}>
          {t('No attendance')}
        </Tag>
      );
    }

    return (
      <div
        style={{
          maxHeight: 120,
          overflowY: attendanceList.length > 4 ? 'auto' : 'visible',
          paddingRight: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {attendanceList.map((a, idx) => {
          let color = 'default';
          if (a.status === 'Present') color = 'green';
          else if (a.status === 'Late') color = 'orange';
          else if (a.status === 'Absent') color = 'red';
          else if (a.status === 'To be rescheduled') color = 'purple';

          const name =
            a.firstname +
            (a.nickname ? ` (${a.nickname})` : '');

          return (
            <Tag
              key={idx}
              color={color}
              style={{ fontSize: 12, whiteSpace: 'normal' }}
            >
              {name} — {a.status}
            </Tag>
          );
        })}
      </div>
    );
  },
},

 {
  title: t('admin confirm'),
  dataIndex: 'adminapprove',
  key: 'adminapprove',
  width: 200,
  render: (status, record) => {
    if (status === 'adminconfirm') {
      return (
        <Space>
          <Tag icon={<CheckCircleOutlined />} color="success">
            {t('Admin Confirmed')}
          </Tag>
          <Popconfirm
            title={t('Revoke Admin Approval')}
            description={t('Are you sure you want to revoke admin approval for this class?')}
            okText={t('Yes')}
            cancelText={t('No')}
            okType="danger"
            onConfirm={() => handleAdminUnapprove(record.classid)}
          >
            <Button
              danger
              size="small"
              shape="circle"
              icon={<CloseOutlined />}
              title={t('Unconfirm')}
            />
          </Popconfirm>
        </Space>
      );
    }

    // Default — Pending (not confirmed yet)
    return (
      <Popconfirm
        title={t('Confirm Admin Approval')}
        description={t('Are you sure you want to confirm this class?')}
        onConfirm={() => handleAdminApprove(record.classid, 'adminconfirm')}
        okText={t('Yes')}
        cancelText={t('No')}
      >
        <Button
          type="primary"
          size="small"
          icon={<CheckOutlined />}
        >
          {t('Admin Confirm')}
        </Button>
      </Popconfirm>
    );
  },
  filters: [
    { text: t('Admin Confirmed'), value: 'adminconfirm' },
    { text: t('Pending'), value: 'pending' },
  ],
  onFilter: (value, record) => record.adminapprove === value,
},
      {
          title: t('approved by'),
          dataIndex: 'admin_nickname',
          key: 'admin_nickname',
          width: 120,
          render: (nickname) => (
            <Tag color="volcano" style={{ fontSize: '11px', padding: '0 6px' }}>
              {nickname || '-'}
            </Tag>
          ),
        }
  ];

  return (
    <div>
      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('Total Classes')}
              value={summaryStats.totalClasses}
              valueStyle={{ color: '#1890ff' }}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('Teacher Confirmed Classes')}
              value={summaryStats.confirmedClasses}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('Teacher Pending Classes')}
              value={summaryStats.pendingClasses}
              valueStyle={{ color: '#faad14' }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('Admin Confirmed')}
              value={summaryStats.adminConfirmed}
              valueStyle={{ color: '#722ed1' }}
              prefix={<CheckOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters and Export */}
      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={24} md={18}>
              <Space wrap size="middle">
                {userRole === "superadmin" && (
                    <Select
                      placeholder={t("Filter by branch")}
                      value={branchFilter}
                      onChange={(value) => setBranchFilter(value || null)}
                      style={{ width: 200 }}
                      allowClear
                    >
                      <Option value="">{t("All Branches")}</Option>
                      {branches.map((branch) => (
                        <Option key={branch.id} value={branch.id}>
                          {branch.name}
                        </Option>
                      ))}
                    </Select>
                  )}

                <Space>
                  <Text strong><CalendarOutlined /> {t('Date Range')}:</Text>
                  
                  {/* Start Date */}
                  <div>
                    <input
                      type="date"
                      id="hidden-start-date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      style={{ display: 'none' }}
                    />
                    <input
                      type="text"
                      readOnly
                      value={formatDate(startDate)}
                      placeholder="27-Jul-25"
                      onClick={() => document.getElementById('hidden-start-date')?.showPicker()}
                      style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #d9d9d9', cursor: 'pointer', width: '120px', textAlign: 'center' }}
                    />
                  </div>

                  <span>-</span>

                  {/* End Date */}
                  <div>
                    <input
                      type="date"
                      id="hidden-end-date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      min={startDate}
                      style={{ display: 'none' }}
                    />
                    <input
                      type="text"
                      readOnly
                      value={formatDate(endDate)}
                      placeholder="27-Jul-25"
                      onClick={() => document.getElementById('hidden-end-date')?.showPicker()}
                      style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #d9d9d9', cursor: 'pointer', width: '120px', textAlign: 'center' }}
                    />
                  </div>
                </Space>

                <Space>
                  <Text strong><UserOutlined /> {t('Tutor')}:</Text>
                  <Select
                    style={{ width: 150 }}
                    value={tutorFilter}
                    onChange={setTutorFilter}
                    placeholder={t('Select Tutor')}
                    allowClear
                    showSearch
                  >
                    <Option value="">{t('All Tutors')}</Option>
                    {getUniqueTutors().map(tutor => (
                      <Option key={tutor} value={tutor}>
                        {tutor}
                      </Option>
                    ))}
                  </Select>
                </Space>

                <Space>
                  <Text strong><CheckCircleOutlined /> {t('Status')}:</Text>
                  <Select
                    style={{ width: 150 }}
                    value={statusFilter}
                    onChange={setStatusFilter}
                    placeholder={t('Select Status')}
                    allowClear
                  >
                    <Option value="">{t('All Statuses')}</Option>
                    <Option value="approved">{t('confirmed')}</Option>
                    <Option value="pending">{t('confirmpending')}</Option>
                  </Select>
                </Space>

                <Space>
                  <Text strong><CheckOutlined /> {t('Admin Status')}:</Text>
                  <Select
                    style={{ width: 180 }}
                    value={adminapproveFilter}
                    onChange={setAdminapproveFilter}
                    placeholder={t('Select Admin Status')}
                    allowClear
                  >
                    <Option value="">{t('All Statuses')}</Option>
                    <Option value="adminconfirm">{t('Admin Confirmed')}</Option>
                    <Option value="pending">{t('Pending')}</Option>
                  </Select>
                </Space>
              </Space>
            </Col>
            
            <Col xs={24} sm={24} md={6} style={{ textAlign: 'right' }}>
              <CSVLink 
                data={csvData} 
                filename={`tutor_class_log_${selectedMonthYear || 'all'}.csv`}
                style={{ textDecoration: 'none' }}
              >
                <Button 
                  type="primary" 
                  icon={<FileExcelOutlined />}
                >
                  {t('exportToCSV')}
                </Button>
              </CSVLink>
            </Col>
          </Row>
        </Space>
      </Card>

      {/* Table */}
      <Card>
        <Table
    columns={columns}
    dataSource={filteredClasses}
    rowKey="classid"
    pagination={pagination}
    onChange={handleTableChange}
    loading={loading}
    scroll={{ x: 'max-content' }}
    size="middle"
    bordered
    footer={() => (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        padding: '12px 0',
        borderTop: '2px solid #f0f0f0',
        backgroundColor: '#fafafa'
      }}>
        <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
          <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
            <ClockCircleOutlined /> {t('Total Hours')}: {formatMinutesToHours(summaryStats.totalHours)}
          </Text>
          <Text style={{ fontSize: '14px', color: '#666' }}>
            ({summaryStats.totalClasses} {t('classes')})
          </Text>
        </div>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <Text style={{ fontSize: '12px', color: '#52c41a' }}>
            ✓ {t('Confirmed')}: {summaryStats.confirmedClasses}
          </Text>
          <Text style={{ fontSize: '12px', color: '#faad14' }}>
            ⏳ {t('Pending')}: {summaryStats.pendingClasses}
          </Text>
          <Text style={{ fontSize: '12px', color: '#722ed1' }}>
            ✅ {t('Admin Confirmed')}: {summaryStats.adminConfirmed}
          </Text>
        </div>
      </div>
    )}
  />
      </Card>
    </div>
  );
};

export default withRoleAccess(TutorClassLog, ['superadmin', 'admin']);